"use client";

import { BookOpen, Building2, Calendar } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface BookResultData {
  key: string;
  title: string;
  author: string;
  authorKeys: string[];
  year: number | null;
  isbn: string | null;
  allIsbns: string[];
  coverUrl: string | null;
  coverUrlSmall: string | null;
  coverUrlLarge: string | null;
  publisher: string | null;
  allPublishers: string[];
  pages: number | null;
  subjects: string[];
  languages: string[];
  editionCount: number;
}

interface BookResultCardProps {
  book: BookResultData;
  onSelect: (book: BookResultData) => void;
}

export function BookResultCard({ book, onSelect }: BookResultCardProps) {
  const [coverError, setCoverError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div className="group relative flex gap-4 rounded-xl border border-border/60 bg-card p-4 transition-all duration-200 hover:border-border hover:shadow-soft-md">
      {/* Cover Thumbnail */}
      <div className="relative shrink-0">
        {book.coverUrlSmall && !coverError ? (
          <div className="relative h-24 w-16 overflow-hidden rounded-md bg-muted/50 shadow-soft">
            {!imageLoaded && (
              <div className="absolute inset-0 animate-pulse bg-muted/70" />
            )}
            <Image
              alt={`Cover of ${book.title}`}
              className={cn(
                "object-cover transition-opacity duration-300",
                imageLoaded ? "opacity-100" : "opacity-0"
              )}
              fill
              onError={() => setCoverError(true)}
              onLoad={() => setImageLoaded(true)}
              sizes="64px"
              src={book.coverUrlSmall}
            />
            <div className="absolute inset-0 rounded-md ring-1 ring-black/5 ring-inset" />
          </div>
        ) : (
          <div className="flex h-24 w-16 items-center justify-center rounded-md border border-border/40 bg-muted/50">
            <BookOpen className="size-6 text-muted-foreground/40" />
          </div>
        )}
      </div>

      {/* Book Info */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex-1">
          <h4 className="mb-1 line-clamp-2 font-medium text-sm leading-snug transition-colors group-hover:text-primary">
            {book.title}
          </h4>
          <p className="line-clamp-1 text-muted-foreground text-xs">
            {book.author}
          </p>
        </div>

        <div className="mt-2 flex items-center gap-3 text-muted-foreground text-xs">
          {book.year && (
            <span className="flex items-center gap-1">
              <Calendar className="size-3" />
              {book.year}
            </span>
          )}
          {book.publisher && (
            <span className="flex max-w-[120px] items-center gap-1 truncate">
              <Building2 className="size-3 shrink-0" />
              <span className="truncate">{book.publisher}</span>
            </span>
          )}
        </div>
      </div>

      {/* Select Button */}
      <div className="flex shrink-0 items-center">
        <Button
          className="opacity-80 transition-opacity group-hover:opacity-100"
          onClick={() => onSelect(book)}
          size="sm"
          type="button"
          variant="outline"
        >
          Select
        </Button>
      </div>
    </div>
  );
}

// Skeleton loading card
export function BookResultCardSkeleton() {
  return (
    <div className="flex gap-4 rounded-xl border border-border/60 bg-card p-4">
      {/* Cover skeleton */}
      <div className="h-24 w-16 shrink-0 animate-pulse rounded-md bg-muted/70" />

      {/* Content skeleton */}
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="h-4 w-3/4 animate-pulse rounded bg-muted/70" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-muted/70" />
        <div className="flex-1" />
        <div className="flex gap-3">
          <div className="h-3 w-12 animate-pulse rounded bg-muted/70" />
          <div className="h-3 w-20 animate-pulse rounded bg-muted/70" />
        </div>
      </div>

      {/* Button skeleton */}
      <div className="flex shrink-0 items-center">
        <div className="h-8 w-16 animate-pulse rounded-md bg-muted/70" />
      </div>
    </div>
  );
}
